// Commune boundaries from the Overpass API (OpenStreetMap).
// Romanian communes are admin_level=8 in OSM.

const OVERPASS_SERVERS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
];
const SERVER_TIMEOUT_MS = 25000;
const STORAGE_KEY = 'communeBoundaries_v1'; // public map data, saved so limits appear instantly next time

const loadSaved = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
};
const cache = loadSaved();
const remember = (name, rings) => {
  cache[name] = rings;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cache)); } catch { /* storage full: still works, just slower next time */ }
};
// About 1 m of precision is plenty for a boundary line, and much smaller to store.
const compact = (rings) => rings.map((ring) => ring.map((p) => ({ lat: Math.round(p.lat * 1e5) / 1e5, lng: Math.round(p.lng * 1e5) / 1e5 })));

// Chain OSM way-geometry arrays into one closed ring.
const assembleRing = (ways) => {
  if (!ways.length) return [];
  let remaining = ways.map((w) => (w.geometry ? [...w.geometry] : [])).filter((g) => g.length);
  if (!remaining.length) return [];
  const ring = [...remaining[0]];
  remaining = remaining.slice(1);

  while (remaining.length > 0) {
    const tail = ring[ring.length - 1];
    let matched = false;
    for (let i = 0; i < remaining.length; i++) {
      const seg = remaining[i];
      const forward = Math.hypot(tail.lat - seg[0].lat, tail.lon - seg[0].lon);
      const reverse = Math.hypot(tail.lat - seg[seg.length - 1].lat, tail.lon - seg[seg.length - 1].lon);
      if (forward < 0.00005) { ring.push(...seg.slice(1)); remaining.splice(i, 1); matched = true; break; }
      if (reverse < 0.00005) { ring.push(...[...seg].reverse().slice(1)); remaining.splice(i, 1); matched = true; break; }
    }
    if (!matched) { ring.push(...remaining[0]); remaining.splice(0, 1); } // gap: append anyway
  }
  return ring.map((p) => ({ lat: p.lat, lng: p.lon }));
};

const buildRings = (element) => {
  const outerWays = element.members?.filter((m) => m.role === 'outer' && m.geometry?.length) || [];
  if (!outerWays.length) return null;
  const ring = assembleRing(outerWays);
  return ring.length ? [ring] : null;
};

// Tries each server in turn, so a busy one doesn't block everything.
const runQuery = async (query) => {
  for (const server of OVERPASS_SERVERS) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SERVER_TIMEOUT_MS);
    try {
      const response = await fetch(server, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: query, signal: controller.signal });
      if (response.ok) {
        const data = await response.json();
        return data.elements?.filter((e) => e.type === 'relation') || [];
      }
    } catch (error) {
      console.warn('Boundary server unavailable, trying the next one:', server);
    } finally {
      clearTimeout(timer);
    }
  }
  throw new Error('No boundary server answered');
};

// Which commune polygon contains this point? Falls back to a name search within ~16 km.
const fetchBoundary = async (name, lat, lng) => {
  if (cache[name]) return cache[name];
  try {
    const containing = await runQuery(
      `[out:json];is_in(${lat},${lng});rel(pivot)["admin_level"="8"]["boundary"="administrative"];out geom;`
    );
    if (containing.length) {
      const rings = buildRings(containing[0]);
      if (rings) { const small = compact(rings); remember(name, small); return small; }
    }

    const buffer = 0.15;
    const bbox = `${lat - buffer},${lng - buffer},${lat + buffer},${lng + buffer}`;
    const byName = (n) => runQuery(
      `[out:json][bbox:${bbox}];relation["name"="${n}"]["admin_level"="8"]["boundary"="administrative"];out geom;`
    );

    let elements = await byName(name);
    if (!elements.length) {
      const plain = name.normalize('NFD').replace(/[̀-ͯ]/g, '');
      if (plain !== name) elements = await byName(plain);
    }
    if (!elements.length) return null;

    let best = null;
    let bestDistance = Infinity;
    elements.forEach((element) => {
      const points = (element.members || []).filter((m) => m.role === 'outer' && m.geometry?.length).flatMap((w) => w.geometry);
      if (!points.length) return;
      const eLat = points.reduce((s, p) => s + p.lat, 0) / points.length;
      const eLon = points.reduce((s, p) => s + p.lon, 0) / points.length;
      const distance = Math.hypot(eLat - lat, eLon - lng);
      if (distance < bestDistance) { bestDistance = distance; best = element; }
    });
    if (!best) return null;

    const rings = buildRings(best);
    if (!rings) return null;
    const small = compact(rings);
    remember(name, small);
    return small;
  } catch (error) {
    console.error('Overpass boundary fetch failed for', name, error);
    return null;
  }
};

// Finds the limits of every commune in the fields. `onProgress` is called after each one,
// so limits appear as they arrive; `isCancelled` stops the work if they're turned off.
export const fetchCommuneBoundaries = async (fields, { onBoundary, onProgress, isCancelled } = {}) => {
  const communes = {};
  (fields || []).forEach((field) => {
    if (!field.comuna || !field.coordinates?.length) return;
    if (!communes[field.comuna]) communes[field.comuna] = { lats: [], lngs: [] };
    field.coordinates.forEach((c) => { communes[field.comuna].lats.push(c.lat); communes[field.comuna].lngs.push(c.lng); });
  });

  const entries = Object.entries(communes);
  const results = [];
  let done = 0;
  onProgress?.(0, entries.length);

  // Saved communes first, so they appear immediately.
  entries.sort(([a], [b]) => Number(Boolean(cache[b])) - Number(Boolean(cache[a])));

  for (const [name, { lats, lngs }] of entries) {
    if (isCancelled?.()) break;
    const lat = lats.reduce((a, b) => a + b) / lats.length;
    const lng = lngs.reduce((a, b) => a + b) / lngs.length;
    const wasSaved = Boolean(cache[name]);
    const rings = await fetchBoundary(name, lat, lng);
    if (isCancelled?.()) break;
    if (rings) { results.push({ name, rings }); onBoundary?.({ name, rings }); }
    done += 1;
    onProgress?.(done, entries.length);
    if (!wasSaved && done < entries.length) await new Promise((resolve) => setTimeout(resolve, 500)); // be polite to Overpass
  }
  return results;
};
